"use client";

import cn from "@utils/cn";
import { getQuarter } from "@utils/dateUtils";
import { DEFAULT_MEASUREMENT, showOnlineExclusiveFeatureFlag } from "@utils/default";
// import { getStructuredDataFromUnits } from "@utils/seo";
import { AnimatePresence, motion } from "framer-motion";
import { useSession } from "next-auth/react";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import React, { FC, useContext, useEffect, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from "react-accessible-accordion";

import AddToFavorites from "@/components/AddToFavorites/AddToFavorites";
import Pagination from "@/components/AvailableUnitsList/Pagination";
import UnitDetails from "@/components/AvailableUnitsList/UnitDetails";
import { useNotification } from "@/components/NotificationProvider/NotificationProvider";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { AvailableUnitContext } from "@/context/AvailableUnitListContext";
import { LoadingContext } from "@/context/LoaderContext";
import { ModalsContext } from "@/context/ModalsContext";
import { UserContext } from "@/context/UserContext";
import { ctaClickTracking, ecomInitiateCheckout, pushMetaversCtaClick } from "@/dataLayer/dataLayer";
import useEnterVirtualTourHandler from "@/hooks/useEnterVirtualTour";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import useMetaverseRouteChangeHandler from "@/hooks/useMetaverse";
import useResponsiveLabel from "@/hooks/useResponsiveLabel";
import { CommercetoolsUnit } from "@/types";

import DesktopFilters from "./DesktopFilters";
import FormattedPrice from "./FormattedPrice";
import { getAvailability } from "./action";

const Modal = dynamic(() => import("@/components/ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("@/components/ui/Modal/ModalContent"), { ssr: false });

interface Props {
  unitName?: string;
  serverSideCookieCurrency?: string;
  currencyFromApiRes?: string;
  isBelongToCommunity?: boolean | void;
  matchedCommunity?: any;
}
const UnitsTable: FC<Props> = ({ unitName, serverSideCookieCurrency, currencyFromApiRes, isBelongToCommunity, matchedCommunity }) => {
  const t = useTranslations();
  const format = useFormatter();
  const router = useRouter();
  const isSo = useIsSaleScreen();
  const { data: session } = useSession();
  const { showUnavailableUnit } = useNotification();
  const { filteredResults, propertyType, projectUrl } = useContext(AvailableUnitContext);
  const { currency, measurement } = useContext(UserContext);
  const { loading } = useContext(LoadingContext);
  const { setEnquireModalOpen, setGetFormPageLocation } = useContext(ModalsContext);
  const [preExpanded, setPreExpanded] = useState<string[] | [] | undefined>(undefined);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isLargeDesktop = useMediaQuery("(min-width: 1630px)");
  const locale = useLocale();
  const [language, _] = locale.split("-");
  const [isLoadingForPrice, setIsLoadingForPrice] = useState(true);
  const [tourModalOpen, setTourModalOpen] = useState<boolean>(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const enquireLabel = useResponsiveLabel(t("stickybar.mobile.enquire"), t("stickybar.desktop.enquire"));
  const parkingLotNumber = (unitname: string) => {
    const parts = unitname.split("/");
    if (parts.length > 1) {
      return parts[1];
    } else {
      return "";
    }
  };

  useEnterVirtualTourHandler(isIframeLoaded);

  useEffect(() => {
    filteredResults?.length && setPreExpanded(filteredResults.length === 1 ? [filteredResults[0].id] : []);
  }, [filteredResults]);
  // const structuredData = !unitName && getStructuredDataFromUnits(filteredResults);

  const handleReserve = async (inventoryID: string, id: string) => {
    loading(true);
    const response = await getAvailability(inventoryID, id);
    console.log("Availability response form units table desktop", response);
    if (response) {
      const unit = filteredResults.find((u) => u.id === id);
      if (unit) {
        const projectTitle = unit.project?.replaceAll(" ", "-").toLowerCase();
        const projectName = unit.propertyName?.replaceAll(" ", "-").toLowerCase();
        const productIdentifier = `${projectTitle ?? ""}/${projectName ?? ""}`;
        ecomInitiateCheckout({
          cta_name: t("unit.reserve.now"),
          form_name: "online_reservation",
          property_name: unit.project,
          project_location: `${unit.area}, ${unit.city}`,
          property_type: unit.identifier,
          bedroom_numbers: unit.bedrooms,
          bathrooms: "no of bathrooms given",
          unit_id: unit.name,
          product_identifier: productIdentifier,
          unit_type: unit.unitType,
          unit_category: unit.isFurnished ? "furnished" : "unfurnished",
          unit_size: unit.areaSqft,
          unit_price: unit.price,
          currency: unit.currency,
          handover_qaurter_year: getQuarter(unit.deliveryDate, locale, t),
          status: unit.status,
          user_logged_in: !!session,
        });
        router.push(`/${locale}/checkout?step=contact`, { scroll: true });
      }
    } else {
      loading(false);
      ctaClickTracking("reserve", "unit page", "property unavailable");
      showUnavailableUnit(filteredResults.length <= 1 ? projectUrl : undefined);
    }
  };

  const formatUnitName = (name: string): string => name.replaceAll("/", "-") || name;
  useEffect(() => {
    if (serverSideCookieCurrency && currencyFromApiRes && currency) {
      if (serverSideCookieCurrency === currencyFromApiRes && currencyFromApiRes === currency) {
        setIsLoadingForPrice(false);
      } else {
        setIsLoadingForPrice(true);
      }
    }
  }, [serverSideCookieCurrency, currencyFromApiRes, currency]);

  return (
    <>
      {filteredResults.length >= 1 && (
        <h2 className="mt-3xl mb-xl text-center text-l md:text-xl uppercase font-primary">
          {filteredResults.length && filteredResults.length === 1 ? t("aul.unitDetails") : t("aul.title")}
        </h2>
      )}
      {/* {!(filteredResults.length && filteredResults.length === 1) && (
        <>
          {isDesktop && (
            <>
              <DesktopFilters unitTypeName={propertyType} />
              <p className="text-center my-xs md:my-xxl font-secondary text-s font-medium">{t("aul.limited.supply")}</p>
            </>
          )}
        </>
      )} */}
      {preExpanded && (
        <div className="hidden md:block">
          <div
            className={`grid ${propertyType !== "Villa" ? "units-grid-first " : "units-grid-second"} border-b border-b-gray-10 mb-xs gap-xs md:p-m`}
          >
            {/* Tags column header (empty) */}
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}></div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}>{t("aul.unitID")}</div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}>{t("aul.unitType")}</div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}>
              {t("aul.bathrooms")}
            </div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}>{t("aul.size")}</div>
            {/* Always render floor column for alignment, empty for Villa */}
            {propertyType !== "Villa" && (
              <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}>
                {propertyType !== "Villa" ? t("aul.floor") : ""}
              </div>
            )}
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}>{t("aul.price")}</div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "text-right" : "text-left"}`}>{t("aul.actions")}</div>
          </div>
          <Accordion allowZeroExpanded preExpanded={preExpanded}>
            <AnimatePresence>
              {filteredResults?.map((unit, index: number) => {
                return (
                  <motion.div
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{ opacity: 1, scaleY: 1 }}
                    exit={{ opacity: 0, scaleY: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    key={unit.id}
                    className="origin-top-left"
                  >
                    <AccordionItem
                      className="rounded-lg even:bg-white md:odd:bg-gray-7 mb-xs"
                      uuid={unit.id}
                      unit-id={unit.id}
                      unit-name={formatUnitName(unit.name)}
                    >
                      <AccordionItemHeading>
                        <AccordionItemButton>
                          <AccordionItemState aria-expanded={filteredResults.length === 1}>
                            {({ expanded }) => (
                              <div
                                className={cn(
                                  `grid group ${propertyType !== "Villa" ? "units-grid-first" : "units-grid-second"} items-center gap-xs transition-colors h-3xl px-m hover:box-material hover:bg-gray-10`,
                                  expanded && "bg-gray-10",
                                )}
                              >
                                {/* Tags column */}
                                <div className={`flex flex-col gap-1 font-secondary text-s ${language === "ar" ? "text-right" : "text-left"}`}>
                                  {unit?.exclusive && showOnlineExclusiveFeatureFlag && (
                                    <span
                                      key={`tag-${index}`}
                                      className="box-material-primary backdrop-blur-xl rounded font-normal uppercase py-3xs px-[6px] text-s text-white before:rounded flex-[0.4] text-center bg-gray-2"
                                    >
                                      {t("filter.label.onlineExclusive")}
                                    </span>
                                  )}
                                  {unit?.goldenVisa === "YES" && (
                                    <span
                                      key={`tag-${index}`}
                                      className="box-material-primary backdrop-blur-xl rounded font-normal uppercase py-3xs px-[6px] text-s text-white before:rounded flex-[0.4] text-center bg-gold-20"
                                    >
                                      {t("filter.label.others.goldenVisaText")}
                                    </span>
                                  )}
                                </div>
                                <div className={`font-secondary text-s ${language === "ar" ? "text-right" : "text-left"}`}>{unit.name}</div>
                                <div className={`font-secondary text-s ${language === "ar" ? "text-right" : "text-left"}`}>{unit.unitType}</div>
                                <div className={`font-secondary text-s ${language === "ar" ? "text-right" : "text-left"}`}>
                                  {unit.bathrooms === -1 ? "" : unit.bathrooms}
                                </div>
                                <div className={`font-secondary text-s ${language === "ar" ? "text-right" : "text-left"}`}>
                                  {measurement === DEFAULT_MEASUREMENT ? Math.round(unit.areaSqft) : Math.round(unit.areaSqmt)}{" "}
                                  {measurement === DEFAULT_MEASUREMENT ? "sq.ft" : "sq.mt"}
                                </div>
                                {/* Always render floor column for alignment, empty for Villa */}
                                {propertyType !== "Villa" && (
                                  <div className={`font-secondary text-s ${language === "ar" ? "text-right" : "text-left"}`}>
                                    {propertyType !== "Villa" ? (unit.floor === -1 ? parkingLotNumber(unit.name) : unit.floor) : ""}
                                  </div>
                                )}
                                <div className={`font-secondary text-s ${language === "ar" ? "text-right" : "text-left"}`}>
                                  {isLoadingForPrice ? t("common.loading") : <FormattedPrice price={unit.price} />}
                                </div>

                                <div className="flex w-full min-w-0 items-center gap-s">
                                  {/*  {expanded && <AddToFavorites item={unit.id} className="relative z-20 flex" fillColor="fill-black" />} */}
                                  {filteredResults.length > 1 && (
                                    <>
                                      <div
                                        className={`flex min-w-0 flex-1 items-center gap-s ${!expanded ? "opacity-0 group-hover:opacity-100" : "opacity-1"}`}
                                      >
                                        <button className="font-secondary text-s underline">
                                          {expanded ? t("button.label.close") : t("button.label.open")}
                                        </button>
                                        {unit?.virtualTourUrl &&
                                          (isLargeDesktop ? (
                                            <>
                                              <Button
                                                size={ButtonSize.MEDIUM}
                                                customClass={`mb-m ${language === "ar" ? "" : "!text-[10px]"}`}
                                                theme={ButtonTheme.BLACK}
                                                type="button"
                                                variant={ButtonVariant.PRIMARY}
                                                style={{ marginBottom: "0" }}
                                                onClick={() => {
                                                  setTourModalOpen(true);
                                                  pushMetaversCtaClick(
                                                    t("stickybar.desktop.viewin3d"),
                                                    unit?.project,
                                                    unit?.city,
                                                    unit?.identifier,
                                                    unit.bedrooms.toString(),
                                                  );
                                                }}
                                              >
                                                {t("stickybar.desktop.viewin3d")}
                                              </Button>
                                              {/* <Button
                                                onClick={() => setTourModalOpen(true)}
                                                rounded
                                                size={ButtonSize.SMALL}
                                                theme={ButtonTheme.WHITE}
                                                type="button"
                                                variant={ButtonVariant.PRIMARY}
                                                // customClass="mr-m"
                                              >
                                                <Icon name="3d" title="3D" size="16" className="stroke-black" />
                                              </Button> */}
                                            </>
                                          ) : (
                                            <div>
                                              <Button
                                                onClick={() => {
                                                  setTourModalOpen(true);
                                                  pushMetaversCtaClick(
                                                    t("stickybar.desktop.viewin3d"),
                                                    unit?.project,
                                                    unit?.city,
                                                    unit?.identifier,
                                                    unit.bedrooms.toString(),
                                                  );
                                                }}
                                                rounded
                                                size={ButtonSize.SMALL}
                                                theme={ButtonTheme.WHITE}
                                                type="button"
                                                variant={ButtonVariant.PRIMARY}
                                                // customClass="mr-m"
                                              >
                                                <Icon name="3d" title="3D" size="16" className="stroke-black" />
                                              </Button>
                                            </div>
                                          ))}
                                      </div>
                                      {!isSo && (
                                        <Button
                                          rounded
                                          size={ButtonSize.MEDIUM}
                                          theme={ButtonTheme.BLACK}
                                          type="button"
                                          variant={ButtonVariant.PRIMARY}
                                          customClass={`md:!px-l md:w-auto ${language === "ar" ? "" : "!text-[10px]"}`}
                                          onClick={() => {
                                            if (unit.sellable) {
                                              handleReserve(unit.inventoryID, unit.id);
                                            } else {
                                              setEnquireModalOpen(true);
                                              setGetFormPageLocation("Property Type Page Lead Form");
                                            }
                                          }}
                                        >
                                          {unit.sellable ? t("unit.reserve.now") : enquireLabel}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                                {unit?.virtualTourUrl && (
                                  <Modal
                                    open={tourModalOpen}
                                    onClose={() => {
                                      setTourModalOpen(false);
                                    }}
                                  >
                                    <ModalContent className="w-full h-full !rounded-none  py-l">
                                      <div className="flex justify-between pb-l">
                                        <h2 className="font-medium uppercase font-primary text-m ">{t("unit.3dtourmodal")}</h2>
                                        <Button
                                          className="modal__close"
                                          size={ButtonSize.MEDIUM}
                                          variant={ButtonVariant.TERTIARY}
                                          theme={ButtonTheme.BLACK}
                                          onClick={() => setTourModalOpen(false)}
                                        >
                                          <span className="pointer-events-none">
                                            <Icon name="close" size="24" title="close" className="stroke-black" />
                                            <span className="sr-only">{t("button.label.close")}</span>
                                          </span>
                                        </Button>
                                      </div>
                                      <iframe
                                        className="object-cover w-full h-[calc(100dvh-100px)] rounded-lg border-0"
                                        width="560"
                                        height="315"
                                        src={unit?.virtualTourUrl}
                                        title="3d Tour"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        onLoad={() => {
                                          setIsIframeLoaded(true);
                                        }}
                                      ></iframe>
                                    </ModalContent>
                                  </Modal>
                                )}
                              </div>
                            )}
                          </AccordionItemState>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        <UnitDetails
                          unit={unit}
                          totalUnits={filteredResults.length}
                          isBelongToCommunity={isBelongToCommunity}
                          matchedCommunity={matchedCommunity}
                        />
                      </AccordionItemPanel>
                    </AccordionItem>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Accordion>
          <Pagination />
        </div>
      )}
      {/* {structuredData && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />} */}
    </>
  );
};
export default UnitsTable;
